/**
 * Work Instructions Page
 *
 * Displays work instructions for production operators.
 * Designed for shop floor use: high contrast, clear labels,
 * minimal cognitive load, task-oriented layout.
 *
 * UX Principles Applied:
 * - Don Norman: Clear affordances, minimize errors
 * - Steve Krug: Don't make users think
 * - Luke Wroblewski: Ruthless simplification
 */

import { useState } from "react";

const API_BASE = "";

export default function WorkInstructionsPage({ onClose }) {
  const [searchInput, setSearchInput] = useState("");
  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const fetchInstructions = async (quote) => {
    if (!quote) return;
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE}/api/pm-center/work-instructions/${encodeURIComponent(quote)}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          setWorkOrder(null);
          setError("No work order found. Try syncing from SharePoint.");
          return;
        }
        throw new Error(`Error loading work order`);
      }

      const data = await response.json();
      setWorkOrder(data);
      setSearchInput(quote);
    } catch (err) {
      setError(err.message);
      setWorkOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    const quote = searchInput.trim();
    if (!quote) return;

    setSyncing(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE}/api/pm-center/work-order-files/link-from-jobboss/${encodeURIComponent(quote)}`,
        { method: "POST" }
      );

      if (!response.ok) {
        throw new Error("Sync failed - check quote number");
      }

      await fetchInstructions(quote);
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const quote = searchInput.trim();
    if (quote) fetchInstructions(quote);
  };

  const skus = workOrder?.skus || [];
  const hasApprovalRequired = skus.some((s) => s.proofing?.clientApprovalRequired);

  return (
    <div className="min-h-screen bg-slate-100 text-gray-900">
      {/* Header - Sticky, always visible */}
      <header className="bg-white border-b-2 border-gray-200 sticky top-0 z-20">
        <div className="max-w-[1800px] mx-auto px-4 py-3 flex items-center gap-4">
          {/* Back button */}
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-900 text-2xl px-2"
              aria-label="Go back"
            >
              ←
            </button>
          )}

          {/* Title area */}
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold">Work Instructions</h1>
            {workOrder && (
              <>
                <span className="text-lg bg-blue-600 text-gray-900 px-3 py-1 rounded font-bold">
                  {workOrder.quoteNumber}
                </span>
                {workOrder.orderNumber && (
                  <span className="text-sm text-gray-500">
                    Order {workOrder.orderNumber}
                  </span>
                )}
              </>
            )}
          </div>

          {/* Search - Right side */}
          <form onSubmit={handleSearch} className="ml-auto flex items-center gap-2">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Quote #"
              className="px-4 py-2 bg-slate-100 border-2 border-gray-300 rounded-lg text-lg w-36 focus:outline-none focus:border-blue-500 font-mono"
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={loading || !searchInput.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-lg font-semibold disabled:opacity-50 min-w-[80px]"
            >
              {loading ? "..." : "Load"}
            </button>
            <button
              type="button"
              onClick={handleSync}
              disabled={syncing || !searchInput.trim()}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm disabled:opacity-50"
            >
              {syncing ? "Syncing..." : "Sync"}
            </button>
          </form>
        </div>

        {/* Alert bar for approval required */}
        {hasApprovalRequired && (
          <div className="bg-yellow-600 text-black px-4 py-2 text-center font-bold text-lg">
            ⚠ CLIENT APPROVAL REQUIRED - Check proofing column
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-[1800px] mx-auto px-4 py-4">
        {/* Error state */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border-2 border-red-600 rounded-lg text-red-200 text-lg">
            {error}
          </div>
        )}

        {/* Empty state */}
        {!loading && !workOrder && !error && (
          <div className="text-center py-20 text-gray-500">
            <div className="text-6xl mb-6">📋</div>
            <div className="text-2xl mb-2">Enter a Quote Number</div>
            <div className="text-lg">to view work instructions</div>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="text-center py-20 text-gray-500">
            <div className="text-3xl">Loading...</div>
          </div>
        )}

        {/* Summary stats */}
        {skus.length > 0 && (
          <div className="mb-4 flex items-center gap-6 text-lg">
            <div className="flex items-center gap-2">
              <span className="text-gray-500">SKUs:</span>
              <span className="font-bold text-2xl">{skus.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Total Qty:</span>
              <span className="font-bold text-2xl">
                {skus.reduce((sum, s) => sum + (s.quantity || 0), 0)}
              </span>
            </div>

            {/* Project folder link */}
            <div className="ml-auto">
              <button
                onClick={() => alert('NAS Project Folder - Coming Soon')}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-100 rounded-lg text-gray-700 hover:text-gray-900"
              >
                <span className="text-xl">📁</span>
                <span>Project Folder</span>
              </button>
            </div>
          </div>
        )}

        {/* SKU Table */}
        {skus.length > 0 && (
          <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-100 border-b-2 border-gray-300">
                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wide w-20">
                    SKU
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wide w-56">
                    Description
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700 uppercase tracking-wide w-16">
                    Qty
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-bold text-yellow-400 uppercase tracking-wide">
                    Production Art
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-bold text-teal-400 uppercase tracking-wide">
                    Finishing & Assembly
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wide w-36">
                    Proofing
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700 uppercase tracking-wide w-24">
                    Files
                  </th>
                </tr>
              </thead>
              <tbody>
                {skus.map((sku, index) => (
                  <SkuRow
                    key={sku.skuNumber}
                    sku={sku}
                    isOdd={index % 2 === 1}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * SKU Row Component
 * Each row shows all critical info for one SKU at a glance.
 * Designed for scanning - key info is prominent, details are structured.
 */
function SkuRow({ sku, isOdd }) {
  const poa = sku.poas?.[0];
  const eq = sku.equipment || {};
  const proof = sku.proofing || {};

  // Equipment indicators - shown as compact badges
  const equipment = [];
  if (eq.epson) equipment.push("Epson");
  if (eq.silver) equipment.push("Silver");
  if (eq.uv && eq.uv !== "N/A" && eq.uv !== "false") equipment.push(`UV:${eq.uv}`);
  if (eq.franklinPress) equipment.push("Franklin");
  if (eq.optiRequired) equipment.push("Opti");
  if (eq.colorDevelopment) equipment.push("ColorDev");

  const rowBg = isOdd ? "bg-white" : "bg-slate-100";
  const needsApproval = proof.clientApprovalRequired;

  return (
    <tr className={`${rowBg} border-b border-gray-200 hover:bg-slate-100/70`}>
      {/* SKU Number - Large, prominent */}
      <td className="px-4 py-4 align-top">
        <div className="text-xl font-bold text-blue-400">{sku.skuNumber}</div>
      </td>

      {/* Description + Equipment badges */}
      <td className="px-4 py-4 align-top">
        <div className="font-medium text-gray-900 text-base leading-snug mb-2">
          {sku.skuDescription || "—"}
        </div>
        {equipment.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {equipment.map((eq, i) => (
              <span
                key={i}
                className="px-2 py-0.5 bg-gray-700 text-gray-700 text-xs rounded font-medium"
              >
                {eq}
              </span>
            ))}
          </div>
        )}
      </td>

      {/* Quantity - Large, centered */}
      <td className="px-4 py-4 align-top text-center">
        <div className="text-2xl font-bold text-gray-900">{sku.quantity || "—"}</div>
      </td>

      {/* Production Art Section */}
      <td className="px-4 py-4" style={{ verticalAlign: 'top' }}>
        {/* PA Instructions - Highlighted box at TOP */}
        {eq.productionArtInstructions && (
          <div className="bg-yellow-500/20 border-l-4 border-yellow-500 p-3 text-sm text-yellow-100 mb-3">
            {eq.productionArtInstructions}
          </div>
        )}

        {/* POA details below instructions */}
        {poa && (
          <div className="text-sm space-y-1">
            <Row label="Type" value={poa.type} />
            <Row label="Substrate" value={poa.substrate} />
            <Row label="Color" value={poa.colorTarget} />
          </div>
        )}

        {/* Multiple POAs indicator */}
        {(sku.poas?.length || 0) > 1 && (
          <div className="text-xs text-gray-500 font-medium mt-2">
            + {sku.poas.length - 1} more POA(s)
          </div>
        )}
      </td>

      {/* Finishing & Assembly Section */}
      <td className="px-4 py-4" style={{ verticalAlign: 'top' }}>
        {/* F&A Instructions - Highlighted box at TOP */}
        {eq.finishingAssemblyInstructions && (
          <div className="bg-teal-500/20 border-l-4 border-teal-500 p-3 text-sm text-teal-100 mb-3">
            {eq.finishingAssemblyInstructions}
          </div>
        )}

        {/* Finish details below instructions */}
        {poa && (poa.appliedFinish || poa.specialEffects) && (
          <div className="text-sm space-y-1">
            {poa.appliedFinish && poa.appliedFinish !== "N/A" && (
              <Row label="Finish" value={poa.appliedFinish} />
            )}
            {poa.specialEffects && poa.specialEffects !== "N/A" && (
              <Row label="Effects" value={poa.specialEffects} />
            )}
          </div>
        )}

        {/* Empty state */}
        {!eq.finishingAssemblyInstructions && !poa?.appliedFinish && !poa?.specialEffects && (
          <span className="text-gray-600">—</span>
        )}
      </td>

      {/* Proofing */}
      <td className="px-4 py-4 align-top">
        {needsApproval ? (
          <div className="bg-yellow-600 text-black px-3 py-2 rounded font-bold text-sm text-center">
            CLIENT APPROVAL
          </div>
        ) : proof.internalReviewOnly ? (
          <div className="text-gray-500 text-sm">Internal Review</div>
        ) : (
          <span className="text-gray-600">—</span>
        )}
        {proof.proofingNotes && (
          <div className="text-xs text-gray-500 mt-2 italic">{proof.proofingNotes}</div>
        )}
      </td>

      {/* File Links */}
      <td className="px-4 py-4 align-top">
        <div className="flex flex-col gap-2">
          <FileButton
            icon="🎨"
            label="Art"
            onClick={() => alert(`File: ${poa?.fileLocation || 'Not specified'}`)}
            title={poa?.fileLocation || "Art file location not specified"}
          />
          <FileButton
            icon="📁"
            label="NAS"
            onClick={() => alert('NAS folder link - Coming Soon')}
            title="Open project folder on NAS"
          />
        </div>
      </td>
    </tr>
  );
}

/**
 * Simple label: value row for structured data display
 */
function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <span className="text-gray-500 w-20 flex-shrink-0">{label}:</span>
      <span className="text-gray-800">{value}</span>
    </div>
  );
}

/**
 * File link button - consistent styling
 */
function FileButton({ icon, label, onClick, title }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="flex items-center justify-center gap-1 px-3 py-2 bg-slate-100 hover:bg-slate-100 rounded text-sm text-gray-500 hover:text-gray-900 transition-colors"
    >
      <span>{icon}</span>
      <span>{label}</span>
    </button>
  );
}
